import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

import { theme } from "../../../utils/global-styles/theme";

import RichTextLink from "./childComponents/RichTextLink";
import RichTextListItem from "./childComponents/RichTextListItem";

const BlurredContentContainer = ({
  blurHeight,
  richText,
  content,
  id,
  padding,
  readMoreLessTitle,
}) => {
  readMoreLessTitle =
    readMoreLessTitle ?  readMoreLessTitle: ["Read More", "Read Less"];
  const [isReadMoreActive, setReadMoreActive] = useState(false);

  const WrapperContainer = styled.div`
      margin: 2em 0;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        margin-left: 0;
        margin-right: 0;
      }
    `,
    ContentContainer = styled.div`
      height: ${blurHeight};
      overflow: hidden;
      position: relative;
      padding: ${padding};

      :after {
        content: "";
        position: absolute;
        height: 74px;
        z-index: 1;
        bottom: 0;
        left: 0;
        right: 0;
        pointer-events: none;
        background-image: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0),
          ${theme.colors.background} 85%
        );
      }

      &.read-more {
        position: static;
        overflow: visible;
        height: max-content;
      }

      @media only screen and (max-width: ${theme.breakpoints.mobile}) {
        padding: 0;
      }
    `,
    ReadMoreTextButton = styled.div`
      display: flex;
      justify-content: center;
      font-size: 1em;
      font-weight: bold;
      line-height: 1.25em;
      cursor: pointer;
      margin-top: 20px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        font-size: 13px;
        font-weight: bold;
        line-height: 16px;
      }
    `,
    RichTextHeader = styled.h4`
      font-weight: bold;
      color: ${theme.colors.main};
      font-size: 35px;
      letter-spacing: -0.91px;
      line-height: 35px;
      margin: 0.5em 0 0.5em 0.5em;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        margin-left: 0;
      }
    `,
    RichTextSubHeader = styled.h5`
      color: ${theme.colors.main};
      font-size: 24px;
      font-weight: bold;
      letter-spacing: -0.22px;
      line-height: 30px;
      margin: 0.75em 0 0.5em 0.75em;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        margin-left: 0;
      }
    `,
    RichTextParagraph = styled.p`
      color: ${theme.colors.main};
      font-size: 20px;
      letter-spacing: -0.57px;
      line-height: 31px;

      @media only screen and (max-width: ${theme.breakpoints.helper800}) {
        margin-left: 0;
      }
    `,
    websiteUrl = typeof window !== `undefined` ? window.location.hostname : "",
    // For a better understanding of the customized rendering below with Rich Text:
    // - https://www.contentful.com/developers/docs/javascript/tutorials/rendering-contentful-rich-text-with-javascript/
    // - https://www.contentful.com/developers/docs/tutorials/general/getting-started-with-rich-text-field-type/
    richTextOptions = {
      renderNode: {
        [BLOCKS.HEADING_2]: (node, children) => {
          return <RichTextHeader>{children}</RichTextHeader>;
        },
        [BLOCKS.HEADING_3]: (node, children) => {
          return <RichTextSubHeader>{children}</RichTextSubHeader>;
        },
        [BLOCKS.PARAGRAPH]: (node, children) => {
          return (
            <RichTextParagraph className="primary-link-container primary-border">
              {children}
            </RichTextParagraph>
          );
        },
        [BLOCKS.LIST_ITEM]: (node, children) => {
          return <RichTextListItem text={children} />;
        },
        [INLINES.HYPERLINK]: node => {
          return (
            <a
              href={node.data.uri}
              target={`${
                node.data.uri.startsWith(websiteUrl) ? "_self" : "_blank"
              }`}
            >
              {node.content[0].value}
            </a>
          );
        },
      },
    };
  return (
    <WrapperContainer>
      <ContentContainer className={isReadMoreActive ? "read-more" : ""} id={id}>
        {richText
          ? documentToReactComponents(richText, richTextOptions)
          : content}
      </ContentContainer>
      <ReadMoreTextButton
        onClick={() => {
          setReadMoreActive(!isReadMoreActive);
        }}

      >
        {isReadMoreActive ? readMoreLessTitle[1] : readMoreLessTitle[0]}
      </ReadMoreTextButton>
    </WrapperContainer>
  );
};

BlurredContentContainer.propTypes = {
  blurHeight: PropTypes.string,
  richText: PropTypes.object,
  content: PropTypes.object,
  id: PropTypes.string,
  padding: PropTypes.string,
  readMoreLessTitle: PropTypes.arrayOf(PropTypes.string),
};

export default BlurredContentContainer;
